import type {
    OrderType,
    SupervisionOrder,
    WorkOrder,
    WorkOrderFilter
} from '@idea2app/ai-work-order-service';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Column, Field, RestForm, RestTable } from 'mobx-restful-table';
import { Component } from 'react';
import { Badge, Button, Card, Container, Modal, Nav } from 'react-bootstrap';

import { ProblemExtractionInfo } from '../component/ProblemExtractionInfo';
import { WorkOrderInfo } from '../component/WorkOrderInfo';
import supervisionOrderStore from '../model/SupervisionOrder';
import { i18n, I18nKey, t } from '../model/Translation';
import workOrderStore from '../model/WorkOrder';

const OrderTypeLabels: Record<OrderType, string> = {
    normal: 'normal_order',
    duplicate: 'duplicate_order',
    sensitive: 'sensitive_order',
    responsible: 'responsible_order'
};

const OrderTypeColors: Record<OrderType, string> = {
    normal: 'success',
    duplicate: 'warning',
    sensitive: 'danger',
    responsible: 'info'
};

@observer
export class SupervisionOrderListPage extends Component {
    @observable
    accessor selectedWorkOrder: WorkOrder | undefined;

    @observable
    accessor activeTab = 'all';

    get filterFields(): Field<WorkOrderFilter>[] {
        return [
            { key: 'keywords', renderLabel: t('search') },
            { key: 'orderNumber', renderLabel: t('order_number') },
            {
                key: 'orderType',
                type: 'select',
                renderLabel: t('order_type'),
                options: [
                    { value: '', label: t('all_statuses') },
                    { value: 'normal', label: t('normal_order') },
                    { value: 'duplicate', label: t('duplicate_order') },
                    { value: 'sensitive', label: t('sensitive_order') },
                    { value: 'responsible', label: t('responsible_order') }
                ]
            },
            { key: 'department', renderLabel: t('department') },
            {
                key: 'satisfactionLevel',
                type: 'select',
                renderLabel: t('satisfaction_level'),
                options: [
                    { value: '', label: t('all_statuses') },
                    { value: 'satisfied', label: t('satisfied') },
                    { value: 'basic', label: t('basic') },
                    { value: 'unsatisfied', label: t('unsatisfied') }
                ]
            }
        ];
    }

    handleTabChange = (key: string | null) => {
        if (!key) return;

        this.activeTab = key;

        workOrderStore.getList({}, 1);
    };

    get columns(): Column<WorkOrder>[] {
        return [
            {
                key: 'id',
                renderHead: t('order_number')
            },
            {
                renderHead: t('supervision_type'),
                renderBody: ({
                    deduplicationResult,
                    sensitiveResult,
                    responsibleResult
                }) => {
                    const orderType = deduplicationResult
                        ? 'duplicate'
                        : sensitiveResult
                          ? 'sensitive'
                          : responsibleResult
                            ? 'responsible'
                            : 'normal';
                    return (
                        <Badge bg={OrderTypeColors[orderType]}>
                            {t(OrderTypeLabels[orderType] as I18nKey)}
                        </Badge>
                    );
                }
            },
            {
                key: 'acceptanceContent',
                renderHead: t('acceptance_content'),
                renderBody: ({ acceptanceContent }) => (
                    <p
                        className="m-0 text-truncate"
                        style={{ maxWidth: '20rem' }}
                    >
                        {acceptanceContent}
                    </p>
                )
            },
            {
                renderHead: t('problem_description'),
                renderBody: ({ acceptanceContent }) => (
                    <p
                        className="m-0 text-truncate"
                        style={{ maxWidth: '20rem' }}
                    >
                        {acceptanceContent}
                    </p>
                )
            },
            {
                key: 'supervisionOrder',
                renderHead: t('supervision_status'),
                renderBody: ({ supervisionOrder }) =>
                    supervisionOrder ? (
                        <Badge bg="success">{t('supervision_completed')}</Badge>
                    ) : (
                        <Badge bg="warning">{t('pending_supervision')}</Badge>
                    )
            },
            {
                key: 'id',
                renderHead: t('operation'),
                renderBody: workOrder => (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => (this.selectedWorkOrder = workOrder)}
                    >
                        {t('create_supervision_order')}
                    </Button>
                )
            }
        ];
    }

    get fields(): Field<SupervisionOrder>[] {
        return [
            { key: 'supervisorName', renderLabel: t('supervisor') },
            {
                key: 'responsibleDepartment',
                renderLabel: t('responsible_department')
            },
            { key: 'recipientName', renderLabel: t('receiver') },
            {
                key: 'templateName',
                renderLabel: t('supervision_template'),
                options: [{ value: '督办单模版1' }]
            }
        ];
    }

    render() {
        const { selectedWorkOrder } = this;

        return (
            <Container className="py-3" fluid>
                <Card className="shadow-sm" body>
                    <hgroup className="d-flex justify-content-between align-items-center mb-3">
                        <h2 className="fs-5 mb-0">
                            {t('pending_supervision_orders')}
                        </h2>
                        <Nav
                            as="nav"
                            variant="pills"
                            activeKey={this.activeTab}
                            onSelect={this.handleTabChange}
                        >
                            <Nav.Link eventKey="all">
                                📋 {t('export_all')}
                            </Nav.Link>
                            <Nav.Link eventKey="duplicate">
                                📋 {t('duplicate_discovery')}
                            </Nav.Link>
                            <Nav.Link eventKey="cancelled">
                                📋 {t('cancelled_orders')}
                            </Nav.Link>
                            <Nav.Link eventKey="listed">
                                📋 {t('listed_orders')}
                            </Nav.Link>
                        </Nav>
                    </hgroup>

                    <RestTable
                        filterFields={this.filterFields}
                        columns={this.columns}
                        store={workOrderStore}
                        translator={i18n}
                        striped
                        hover
                    />
                </Card>

                <Modal
                    size="lg"
                    show={!!selectedWorkOrder}
                    onHide={() => (this.selectedWorkOrder = undefined)}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>{t('new_supervision_order')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <WorkOrderInfo {...selectedWorkOrder} />
                        <ProblemExtractionInfo
                            {...selectedWorkOrder?.sensitiveResult}
                        />
                        <RestForm
                            translator={i18n}
                            store={supervisionOrderStore}
                            fields={this.fields}
                        />
                    </Modal.Body>
                </Modal>
            </Container>
        );
    }
}
